// Per-item extraction panel for UNSTRUCTURED sources (slack/gmail).
//
// Unlike db/api sources (one static schema+mapping for the whole source), an
// unstructured source has no fixed schema: each message/email is filtered, and
// for EACH item an LLM decides which data_elements it provides and extracts the
// VALUE per element. This panel drives that flow:
//   - "Process messages" → POST /data_source/:id/process_items (incremental,
//     capped) → engine fetches new items, LLM-extracts {element: value} per item,
//     backend persists to cortex_ops.source_item_extraction.
//   - HISTORY list ← GET /data_source/:id/extractions — each item expandable to
//     its dynamic element→value mappings.
import React, { useState, useEffect, useCallback } from "react";
import { Loader2, RefreshCw, Play, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { fetchData } from "@/fetchapi/fetchapi";

function ExtractionRow({ item }) {
  const [open, setOpen] = useState(false);
  const extracted = Array.isArray(item.extracted) ? item.extracted : [];
  const when = item.received_at ? new Date(item.received_at).toLocaleString() : "—";
  return (
    <div className="border-b border-page-divider/50">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 py-2 text-left cursor-pointer bg-transparent border-0 hover:opacity-80">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="text-xs font-mono text-page-muted shrink-0">{when}</span>
        <span className="text-sm truncate flex-1" style={{ color: "var(--page-text)" }}>
          {item.raw_excerpt || item.external_id}
        </span>
        <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap shrink-0"
          style={{ background: extracted.length ? "rgba(77,179,0,0.15)" : "var(--card-bg)", color: extracted.length ? "#2f7d00" : "var(--page-muted)" }}>
          {extracted.length} mapped
        </span>
      </button>
      {open && (
        <div className="pb-3 pl-6">
          {item.raw_excerpt && (
            <pre className="text-xs whitespace-pre-wrap text-page-muted bg-page-bg rounded-[5px] p-2 mb-2 max-h-40 overflow-auto">{item.raw_excerpt}</pre>
          )}
          {extracted.length === 0 ? (
            <div className="text-page-muted text-xs">No data elements extracted from this item.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-page-muted border-b border-page-divider">
                <th className="py-1">Data element</th><th>Value</th><th className="w-20">Confidence</th></tr></thead>
              <tbody>
                {extracted.map((e, i) => (
                  <tr key={i} className="border-b border-page-divider/40">
                    <td className="py-1 font-mono text-xs">{e.element_name}</td>
                    <td className="text-xs break-words">{String(e.value)}</td>
                    <td className="text-xs">{e.confidence != null ? `${Math.round(e.confidence * 100)}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function UnstructuredItemsPanel({ sourceId, color }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchData(`data_source/${sourceId}/extractions`, { limit: 50 });
      setRows(d?.rows || []);
      setTotal(d?.total || 0);
    } catch (e) {
      // 503 / missing table on a fresh deploy is non-fatal — just show empty.
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [sourceId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const processMessages = async () => {
    setProcessing(true);
    try {
      const d = await fetchData(`data_source/${sourceId}/process_items`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit: 50 }),
      });
      const n = d?.inserted ?? 0;
      toast.success(n > 0 ? `Extracted ${n} new item${n === 1 ? "" : "s"}` : "No new items to process");
      await loadHistory();
    } catch (e) {
      toast.error(`Processing failed: ${e?.message || e}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-sm text-page-muted">
          Each message is mapped & extracted individually — {total} processed.
        </p>
        <div className="flex items-center gap-2">
          <button onClick={loadHistory} disabled={loading}
            className="flex items-center gap-2 h-9 px-3 rounded-[5px] border border-page-divider text-sm cursor-pointer bg-card-bg hover:border-nav-active transition-colors disabled:opacity-50">
            {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Refresh
          </button>
          <button onClick={processMessages} disabled={processing}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 hover:bg-nav-active-dark transition-colors disabled:opacity-50">
            {processing ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}
            {processing ? "Processing…" : "Process messages"}
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={14} />Loading history…</div>
      ) : rows.length === 0 ? (
        <div className="text-page-muted text-sm">No items processed yet. Set the filter in Connection, then click “Process messages” to fetch & extract.</div>
      ) : (
        <div>{rows.map((r) => <ExtractionRow key={r.id} item={r} />)}</div>
      )}
    </div>
  );
}
